import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useContext, useState, lazy, Suspense } from "react";
import { initViewportVars } from "./utils/viewport";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import ErrorBoundary from "./components/ErrorBoundary";

import { AuthProvider, AuthContext } from "./context/AuthContext";
import AppRouteFallback from "./components/shells/AppRouteFallback";
import { warmCommonRouteChunks } from "./utils/routeWarmup";

import { TimeProvider } from "./context/TimeContext";

const Home = lazy(() => import("./pages/Home"));
const Info = lazy(() => import("./pages/Info"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const AddTopic = lazy(() => import("./pages/AddTopic"));
const AdminProfile = lazy(() => import("./pages/AdminProfile"));
const TopicView = lazy(() => import("./pages/TopicView"));
const TopicEdit = lazy(() => import("./pages/TopicEdit"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const SuggestTopic = lazy(() => import("./pages/SuggestTopic"));
const ReviewSuggestions = lazy(() => import("./pages/ReviewSuggestions"));
const ReviewFlags = lazy(() => import("./pages/ReviewFlags"));
const ActivitySuggested = lazy(() => import("./pages/ActivitySuggested"));
const ActivityAccepted = lazy(() => import("./pages/ActivityAccepted"));
const ActivityEdits = lazy(() => import("./pages/ActivityEdits"));
const ActivityCoEdits = lazy(() => import("./pages/ActivityCoEdits"));
const TopicWordPage = lazy(() => import("./pages/TopicWordPage"));
const RootSlugPage = lazy(() => import("./pages/RootSlugPage"));
const AdminSendWarning = lazy(() => import("./pages/admin/AdminSendWarning"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminFlaggedTopics = lazy(() => import("./pages/admin/AdminFlaggedTopics"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminReviewTopic = lazy(() => import("./pages/admin/AdminReviewTopic"));
const AdminUnderReviewTopics = lazy(() => import("./pages/admin/AdminUnderReviewTopics"));
const AdminAllTopics = lazy(() => import("./pages/admin/AdminAllTopics"));
const AdminUserActivity = lazy(() => import("./pages/admin/AdminUserActivity"));
const AdminUserActivityDetail = lazy(() => import("./pages/admin/AdminUserActivityDetail"));
const AdminSearchTrends = lazy(() => import("./pages/admin/AdminSearchTrends"));
const AdminAnonymousVisitorDetail = lazy(() => import("./pages/admin/AdminAnonymousVisitorDetail"));
const AdminDeletedUsers = lazy(() => import("./pages/admin/AdminDeletedUsers"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const AdminWebsiteHealth = lazy(() => import("./pages/admin/AdminWebsiteHealth"));
const AdminTopicNamesExport = lazy(() => import("./pages/admin/AdminTopicNamesExport"));

import { buildProfilePath } from "./utils/publicPaths";
// Use the single shared HTTP client (cookie auth via withCredentials in api.js).
import api from "./api/api";
import "./App.css";

function ProfileSwitch() {
  const { user } = useContext(AuthContext);

  const isAdminOrMod = user?.role === "admin" || user?.role === "moderator";

  return (
    <Suspense fallback={<AppRouteFallback />}>
      {isAdminOrMod ? <AdminProfile /> : <Profile />}
    </Suspense>
  );
}

function MyProfileCanonicalRedirect() {
  const { user } = useContext(AuthContext);

  if (!user?.username) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={buildProfilePath(user)} replace />;
}
// WarningGate:
// - Fetch notifications
// - If there is any unread "warning_required", block UI with a modal
// - User must click "I Understand" to mark it read
function WarningGate() {
  // English: Cookie session auth -> we only need user presence
  const { user } = useContext(AuthContext);

  const [blockingNotif, setBlockingNotif] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBlockingWarning = async () => {
    if (!user) return;

    try {
      setLoading(true);
      // Use api instance; cookies are sent automatically (withCredentials in api.js).
const res = await api.get("/api/notifications");

const payload = res.data;
const list = Array.isArray(payload) ? payload : payload?.notifications || [];

      // Find the newest unread warning_required notification
      const warning = list.find(
        (n) => !n.is_read && (n.type === "warning_required" || n.type === "warning")
      );

      setBlockingNotif(warning || null);
    } catch (e) {
      console.error("WarningGate fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Re-check on route change (so user cannot bypass by navigation)
  useEffect(() => {
    // Re-check blocking warnings whenever session user changes.
    fetchBlockingWarning();
  }, [user]);

  const acknowledge = async () => {
    if (!blockingNotif) return;

    try {
      // Mark notification as read via api client (cookie session).
await api.put(`/api/notifications/${blockingNotif.id}/read`, {});

      // Refresh and see if more warnings exist
      await fetchBlockingWarning();
    } catch (e) {
      console.error("Acknowledge failed:", e);
    }
  };

  if (!user) return null;

  // If no blocking warning, allow normal app usage
  if (!blockingNotif) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          background: "#fff",
          borderRadius: 12,
          padding: 18,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>⚠️ Action Required</h3>

        {/* Split message into 3 parts:
    1) Topic line (bold)
    2) Custom warning text (MOST important -> bigger/bolder)
    3) Generic line (less important -> smaller + lighter)
*/}
{(() => {
  const raw = (blockingNotif?.message || "").trim();
  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);

  // Expected format from backend:
  // lines[0] = Topic "name"
  // lines[1] = custom warning (optional)
  // lines[last] = generic line (always)
  const topicLine = lines[0]?.startsWith("Topic ") ? lines[0] : "";
  const genericLine = lines.length >= 2 ? lines[lines.length - 1] : "";

  // Everything between topicLine and genericLine is treated as custom warning text
  const customLines =
    lines.length > 2 ? lines.slice(1, -1) : (lines.length === 2 ? [lines[1]] : []);

  // If backend sent only topic + generic, custom can be empty
  const customText = customLines.join("\n").trim();

  return (
    <>
      {/* Topic name line */}
      {topicLine ? (
        <p style={{ marginBottom: 8, fontWeight: 700 }}>{topicLine}</p>
      ) : null}

      {/* Custom warning text (HIGH priority) */}
      {customText ? (
  <p
    style={{
      marginBottom: 10,

      // Professional typography (simple, readable)
      fontWeight: 700,
      fontSize: 15,
      lineHeight: 1.35,

      // Keep it clearly "primary" but not weird
      background: "rgba(0,0,0,0.04)",
      borderLeft: "4px solid rgba(0,0,0,0.25)",
      padding: "10px 12px",
      borderRadius: 8,

      // CRITICAL: prevent overflow for long unbroken text
      whiteSpace: "pre-line",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    }}
  >
    {customText}
  </p>
   ) : null}

      {/* Generic line (LOW priority / secondary) */}
      {genericLine ? (
  <p
    style={{
      marginBottom: 12,

      // Secondary / less attention
      fontSize: 12.5,
      lineHeight: 1.35,
      opacity: 0.65,

      // Prevent overflow too (safety)
      whiteSpace: "pre-line",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    }}
  >
    {genericLine}
  </p>
) : null}

    </>
  );
  })()}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          {/* No cancel button: must acknowledge */}
          <button
            className="logout-btn"
            onClick={acknowledge}
            disabled={loading}
            title="You must acknowledge this warning"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
    // /info ko "modal over Home" ki tarah show karna hai.
  const routeFallback = <AppRouteFallback />;
  const isInfoRoute = location.pathname === "/info";
  const backgroundLocation = location.state?.backgroundLocation;

  // Agar direct /info open hua (no backgroundLocation), tab bhi background me Home dikhna chahiye.
  const routesLocation =
    backgroundLocation || (isInfoRoute ? { ...location, pathname: "/" } : location);

  const isInfoModalOpen = isInfoRoute;

    useEffect(() => {
    // English:
    // Warm the most common route chunks after first paint.
    // This improves perceived speed on mobile without blocking initial render.
    let cancelled = false;
    let idleId = null;
    let timerId = null;

    const runWarmup = () => {
      if (cancelled) return;
      warmCommonRouteChunks();
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(runWarmup, { timeout: 1500 });
    } else {
      timerId = window.setTimeout(runWarmup, 700);
    }

    return () => {
      cancelled = true;

      if (
        idleId !== null &&
        typeof window !== "undefined" &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(idleId);
      }

      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

    const hideFooter = false;

  return (
      <div className={`app-shell ${isInfoModalOpen ? "modal-open" : ""}`}>
           <Navbar />
      <WarningGate />

      <main
        className={`app-main ${
          hideFooter ? "app-main--no-footer" : "app-main--with-footer"
        }`}
      >
        <div className="app-main-content">
          <ScrollToTop />

          <ErrorBoundary>
            {/* Background routes (normally current location, but /info par background me Home) */}
            <Suspense fallback={routeFallback}>
  <Routes location={routesLocation}>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            {/* NOTE: /info yahan nahi, kyunki ye modal banega */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Legacy topic/profile public routes. Keep only as fallback support. */}
<Route path="/topic/word" element={<Navigate to="/" replace />} />
<Route path="/topic/word/:word" element={<TopicWordPage />} />
<Route path="/topic/:id" element={<TopicView />} />
<Route
  path="/user/:id"
  element={
    <ProtectedRoute>
      <UserProfile />
    </ProtectedRoute>
  }
/>

            {/* Protected routes */}
            <Route
  path="/profile"
  element={
    <ProtectedRoute>
      <MyProfileCanonicalRedirect />
    </ProtectedRoute>
  }
/>
            <Route
              path="/delete-account"
              element={
                <ProtectedRoute>
                  <DeleteAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-topic"
              element={
                <ProtectedRoute>
                  <AddTopic />
                </ProtectedRoute>
              }
            />

            <Route
              path="/topic/:id/suggest"
              element={
                <ProtectedRoute>
                  <SuggestTopic />
                </ProtectedRoute>
              }
            />

            <Route
              path="/topic/:id/edit"
              element={
                <ProtectedRoute>
                  <TopicEdit />
                </ProtectedRoute>
              }
            />

            <Route
              path="/topic/:id/review-suggestions"
              element={
                <ProtectedRoute>
                  <ReviewSuggestions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/topic/:id/review-flags"
              element={
                <ProtectedRoute>
                  <ReviewFlags />
                </ProtectedRoute>
              }
            />

            {/* Activity */}
            <Route
              path="/activity/suggested"
              element={
                <ProtectedRoute>
                  <ActivitySuggested />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity/accepted"
              element={
                <ProtectedRoute>
                  <ActivityAccepted />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity/edits"
              element={
                <ProtectedRoute>
                  <ActivityEdits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity/co-edits"
              element={
                <ProtectedRoute>
                  <ActivityCoEdits />
                </ProtectedRoute>
              }
            />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/topics"
              element={
                <AdminRoute>
                  <AdminAllTopics />
                </AdminRoute>
              }
            />
            <Route
  path="/admin/topic-names"
  element={
    <AdminRoute>
      <AdminTopicNamesExport />
    </AdminRoute>
  }
/>
            <Route
              path="/admin/topics/:id/warn"
              element={
                <AdminRoute>
                  <AdminSendWarning />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/flags"
              element={
                <AdminRoute>
                  <AdminFlaggedTopics />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <AdminRoute>
                  <AdminLogs />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/under-review"
              element={
                <AdminRoute>
                  <AdminUnderReviewTopics />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/review/:id"
              element={
                <AdminRoute>
                  <AdminReviewTopic />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users-activity"
              element={
                <AdminRoute>
                  <AdminUserActivity />
                </AdminRoute>
              }
            />
            <Route
  path="/admin/users-activity/:id"
  element={
    <AdminRoute>
      <AdminUserActivityDetail />
    </AdminRoute>
  }
/>
            <Route
              path="/admin/search-trends"
              element={
                <AdminRoute>
                  <AdminSearchTrends />
                </AdminRoute>
              }
            />
            <Route
  path="/admin/website-health"
  element={
    <AdminRoute>
      <AdminWebsiteHealth />
    </AdminRoute>
  }
/>
            <Route
  path="/admin/search-trends/anonymous/:anonId"
  element={
    <AdminRoute>
      <AdminAnonymousVisitorDetail />
    </AdminRoute>
  }
/>
            <Route
              path="/admin/deleted-users"
              element={
                <AdminRoute>
                  <AdminDeletedUsers />
                </AdminRoute>
              }
            />
            <Route path="/:slug" element={<RootSlugPage />} />
            {/* 404 */}
            <Route
              path="*"
              element={
                <div style={{ paddingTop: "80px", textAlign: "center" }}>
                  <h2>404 - Page not found</h2>
                  <p>Galat URL hai. Navbar se koi valid page open karo.</p>
                </div>
              }
            />
            </Routes>
</Suspense>

                     {/* Modal routes (overlay) */}
            {isInfoModalOpen ? (
             <Suspense fallback={routeFallback}>
  <Routes>
    <Route path="/info" element={<Info />} />
  </Routes>
</Suspense>
            ) : null}
          </ErrorBoundary>
        </div>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
}

function App() {
  useEffect(() => {
    const cleanup = initViewportVars();
    return cleanup;
  }, []);

  return (
    <TimeProvider intervalMs={60_000}>
      <AuthProvider>
        <Router>
          <AppLayout />
        </Router>
      </AuthProvider>
    </TimeProvider>
  );
}

export default App;
 
